import { useEffect } from 'react';
import { useEditMode } from '../contexts/EditModeContext';
import type { CmsBlock } from '../types/cms';

type IncomingMessage =
  | { type: 'BLOCK_UPDATE'; blockId: string; props: Record<string, unknown> }
  | { type: 'BLOCK_TREE'; blocks: CmsBlock[] }
  | { type: 'BLOCK_DELETE'; blockId: string };

/**
 * Listens for postMessage commands from the Visual Builder (admin-UI parent window)
 * and dispatches them to EditModeContext. Also signals READY on mount.
 *
 * Only active when editMode=true is present in the URL.
 */
export function useParentMessages() {
  const { isEditMode, dispatch } = useEditMode();

  useEffect(() => {
    if (!isEditMode) return;

    function handleMessage(event: MessageEvent) {
      // Accept only from direct parent (the builder iframe container)
      if (event.source !== window.parent) return;

      const msg = event.data as IncomingMessage;
      if (!msg?.type) return;

      switch (msg.type) {
        case 'BLOCK_TREE':
          dispatch({ type: 'SET_BLOCKS', blocks: msg.blocks });
          break;
        case 'BLOCK_UPDATE':
          dispatch({ type: 'UPDATE_BLOCK', blockId: msg.blockId, props: msg.props });
          break;
        case 'BLOCK_DELETE':
          dispatch({ type: 'DELETE_BLOCK', blockId: msg.blockId });
          break;
      }
    }

    window.addEventListener('message', handleMessage);
    // Signal the builder that the website is ready to receive commands
    window.parent.postMessage({ type: 'READY' }, '*');

    // Report content height to parent so iframe container can resize.
    // Only grow, never shrink (prevents flicker when images load async).
    // Use ResizeObserver on body which fires when actual layout changes.
    let lastReportedHeight = 0;
    function reportHeight() {
      const h = document.documentElement.scrollHeight;
      // Only report if height grew (never shrink — avoids flicker during image loads)
      if (h <= lastReportedHeight) return;
      lastReportedHeight = h;
      window.parent.postMessage({ type: 'CONTENT_HEIGHT', height: h }, '*');
    }

    // Initial report
    reportHeight();

    // ResizeObserver on body detects layout shifts (including image loads)
    const resizeObs = new ResizeObserver(() => reportHeight());
    resizeObs.observe(document.body);

    // MutationObserver for new DOM nodes (e.g. new blocks added)
    const mutObs = new MutationObserver(() => reportHeight());
    mutObs.observe(document.body, { childList: true, subtree: true });

    // Also catch image loads which change layout without DOM mutation
    document.addEventListener('load', reportHeight, true); // capture phase catches img loads

    return () => {
      window.removeEventListener('message', handleMessage);
      resizeObs.disconnect();
      mutObs.disconnect();
      document.removeEventListener('load', reportHeight, true);
    };
  }, [isEditMode, dispatch]);
}
